import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { createSharedComposable } from '@vueuse/core';
import { request } from '@/api/request';
import { useStore } from '@/store/useStore';
import useFeatureFlags, { FeatureFlag } from '@/composables/feature-flags';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  retryPageReadRequirement,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import type { Control, ScanTypeControlPair } from '@@/packages/shared/types/models/control';
import type { Domain } from '@@/packages/shared/types/models/domain';
import { deriveRequiredDomainIDs, deriveRequiredControlIDs } from '../requiredControls';
import type {
  CombinedFrameworkConfig,
  FrameworkConfig,
  FrameworkLevel,
  FrameworkRequirementsConnection,
} from '@@/packages/shared/types/models/frameworkConfig';
import { SUPPLIER_INFO_QUERY } from '@/modules/org-info/api/queryKeys';
import {
  SUPPLIER_ASSESSMENT_ANSWERS_QUERY,
  SUPPLIER_ASSESSMENT_OVERRIDES_QUERY,
  SUPPLIER_ASSESSMENT_REMEDIATIONS_QUERY,
  SUPPLIER_ASSESSMENT_SCOPING_ANSWERS_QUERY,
  SUPPLIER_COMMUNITY_SIGNALS_QUERY,
  SUPPLIER_COMPLIANCE_VTWO_QUERY,
  SUPPLIER_REASSESSMENT_STATUS_QUERY,
} from '@/modules/profile/api/queryKeys';
import { SUPPLIERS_QUERY } from '@/modules/connection-view-suppliers/api/queryKeys';
import { CLIENT_DASHBOARD_QUERY } from '@/modules/client-dashboard/api/queryKeys';
import { RISKS_QUERY } from '@/modules/risks/api/queryKeys';
import {
  COMBINED_FRAMEWORK_CONFIG_QUERY,
  FLEXIBLE_PREVIEW_QUERY,
  FRAMEWORK_CONFIG_QUERY,
  FRAMEWORK_DEFAULT_CONFIG_QUERY,
  FRAMEWORK_QUERY,
  FRAMEWORK_REQUIREMENTS_QUERY,
} from '../queryKeys';

export type GetFrameworkResponse = {
  controls: Control[];
  domains: Domain[];
  levels: FrameworkLevel[];
  scanTypeControlPairs: ScanTypeControlPair[];
};

// How the platform names a control everywhere: domain letter and control number.
function controlAndDomainName(control: Control, domain: Domain) {
  return {
    id: control.controlID,
    name: `${domain.letter}.${control.number}`,
    domainLetter: domain.letter,
    domainName: domain.name,
  };
}

export const useFramework = createSharedComposable(() => {
  const query = useQuery({
    queryKey: [FRAMEWORK_QUERY],
    async queryFn() {
      const res = await request<GetFrameworkResponse>('GET', '/framework');
      return res.data;
    },
    staleTime: Infinity,
    ...retryPageReadRequirement(),
  });

  // Update the vuex store for legacy code to use
  const store = useStore();
  watchEffect(() => {
    if (store && query.data.value) store.commit('framework/SET_FRAMEWORK', query.data.value);
  });

  const domains = computed(() => query.data.value?.domains ?? []);

  // This should NOT be exposed and is for internal optimisation only.
  // If you want to iterate, use `sortedDomains`. If you want to look up, use
  // `domainWithID`. It's very intentional that it throws - it will simplify
  // your types. IDs don't change and aren't removed, you don't need to worry
  // about the error.
  const domainsByID = computed(() =>
    Object.fromEntries(domains.value.map((domain) => [domain.domainID, domain])),
  );

  const sortedDomains = computed<Domain[]>(() =>
    domains.value
      .concat()
      .sort((a, b) => {
        if (a.letter === b.letter) return 0;
        return a.letter > b.letter ? 1 : -1;
      })
      .filter((domain) => !domain.deprecated),
  );

  const addOnDomains = computed(() => domains.value.filter((domain) => domain.isAddOn));

  function domainWithID(domainID: string) {
    const domain = domainsByID.value[domainID];
    if (!domain) throw new Error("Domain doesn't exist");
    return domain;
  }

  const domainsByLetter = computed(() =>
    domains.value.reduce<Record<string, Domain>>((obj, domain) => {
      obj[domain.letter] = domain;
      return obj;
    }, {}),
  );

  const controls = computed(() => query.data.value?.controls ?? []);

  // This should NOT be exposed and is for internal optimisation only.
  // See `domainsByID` for explanation
  const controlsByID = computed(() =>
    Object.fromEntries(controls.value.map((control) => [control.controlID, control])),
  );

  const sortedControls = computed(() =>
    controls.value
      .concat()
      .sort((a, b) => {
        const x = domainWithID(a.domainID).letter;
        const y = domainWithID(b.domainID).letter;
        if (x === y) return a.number - b.number;
        return x > y ? 1 : -1;
      })
      .filter((control) => !control.deprecated),
  );

  const sortedControlsDeprecated = computed(() =>
    controls.value
      .concat()
      .sort((a, b) => {
        const x = domainWithID(a.domainID).letter;
        const y = domainWithID(b.domainID).letter;
        if (x === y) return a.number - b.number;
        return x > y ? 1 : -1;
      })
      .filter((c) => c.deprecated || domainWithID(c.domainID).deprecated),
  );

  function controlWithID(controlID: string) {
    const control = controlsByID.value[controlID];
    if (!control) throw new Error("Control doesn't exist");
    return control;
  }

  // Does not include deprecated controls
  const controlsByDomain = computed(() =>
    domains.value.reduce<Record<string, string[]>>((obj, domain) => {
      obj[domain.domainID] = sortedControls.value
        .filter((control) => control.domainID === domain.domainID && !control.deprecated)
        .map((control) => control.controlID);
      return obj;
    }, {}),
  );

  const controlsByScanTypeID = computed(() => {
    const pairs = query.data.value?.scanTypeControlPairs || [];
    const controlsByScanType: Record<string, Control[]> = {};

    for (const pair of pairs) {
      const control = controlWithID(pair.controlID);
      const existingControl = controlsByScanType[pair.scanTypeID];
      if (existingControl) {
        existingControl.push(control);
      } else {
        controlsByScanType[pair.scanTypeID] = [control];
      }
    }

    return controlsByScanType;
  });

  const scanTypeIDsByControlID = computed(() => {
    const pairs = query.data.value?.scanTypeControlPairs || [];
    const scanTypesByControl: Record<string, string[]> = {};

    for (const pair of pairs) {
      const existingScanType = scanTypesByControl[pair.controlID];
      if (existingScanType) {
        existingScanType.push(pair.scanTypeID);
      } else {
        scanTypesByControl[pair.controlID] = [pair.scanTypeID];
      }
    }

    return scanTypesByControl;
  });

  const controlsByDomainDeprecated = computed(() =>
    domains.value.reduce<Record<string, string[]>>((obj, domain) => {
      obj[domain.domainID] = sortedControlsDeprecated.value
        .filter((control) => control.domainID === domain.domainID)
        .map((control) => control.controlID);
      return obj;
    }, {}),
  );

  // Does not include deprecated controls
  const dependentControls = computed(() =>
    sortedControls.value.reduce<Record<string, string[]>>((obj, c) => {
      if (!c.parentID) return obj;
      // oxlint-disable-next-line no-non-null-assertion
      if (Array.isArray(obj[c.parentID])) obj[c.parentID]!.push(c.controlID);
      else obj[c.parentID] = [c.controlID];
      return obj;
    }, {}),
  );

  // Lookup whether a controlID is a parent of dependent controls or not
  const isParentControl = (controlID: string) =>
    Array.isArray(dependentControls.value[controlID]) &&
    dependentControls.value[controlID].length > 0;

  // Lookup whether a controlID is a dependent control or not
  const isChildControl = (controlID: string) => !!controlWithID(controlID).parentID;

  // Lookup whether a controlID is the last child of its parent
  const isLastChildControl = (controlID: string) => {
    const children = dependentControls.value[controlWithID(controlID).parentID];
    if (!children) return undefined;
    return children[children.length - 1] === controlID;
  };

  const certificationsList = computed(() => [
    ...new Set(
      controls.value.reduce<string[]>((obj, control) => {
        if (control.certifications) obj.push(...control.certifications);
        return obj;
      }, []),
    ),
  ]);

  // Takes controlID as a param returns new object with domain letter & control
  // { id: controlID, name: 'A.3', domainName: 'Security' }
  const getControlAndDomainName = (controlID: string) => {
    const control = controlWithID(controlID);
    return controlAndDomainName(control, domainWithID(control.domainID));
  };

  // As above, but undefined rather than throwing for a control this build has no
  // entry for. Data recorded against another framework version can name one, and
  // that has to read as absent rather than crash.
  const findControlAndDomainName = (controlID: string) => {
    const control = controlsByID.value[controlID];
    const domain = control && domainsByID.value[control.domainID];
    return control && domain ? controlAndDomainName(control, domain) : undefined;
  };

  return {
    ...query,
    domains,
    sortedDomains,
    domainWithID,
    domainsByLetter,
    controls,
    sortedControls,
    sortedControlsDeprecated,
    controlWithID,
    controlsByScanTypeID,
    scanTypeIDsByControlID,
    controlsByDomain,
    controlsByDomainDeprecated,
    dependentControls,
    isParentControl,
    isChildControl,
    isLastChildControl,
    certificationsList,
    getControlAndDomainName,
    findControlAndDomainName,
    addOnDomains,
    levels: computed(() => query.data.value?.levels || []),
  };
});

export function useFrameworkDefaultConfig(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [FRAMEWORK_DEFAULT_CONFIG_QUERY],
      async queryFn() {
        const res = await request<FrameworkConfig>('GET', '/framework/config/default');
        return res.data;
      },
    }),
  );

  return {
    ...query,
    addOnDomains: computed(() => query.data.value?.addOnDomains || []),
    level: computed(() => query.data.value?.level),
  };
}

export function useUpdateFrameworkDefaultConfig(
  options?: MutationOptions<FrameworkConfig, FrameworkConfig>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(config) {
        const res = await request<FrameworkConfig>('PUT', '/framework/config/default', config);
        return res.data;
      },
      async onSuccess(_data, variables) {
        await queryClient.setQueryData(
          [FRAMEWORK_DEFAULT_CONFIG_QUERY],
          (oldData: FrameworkConfig | undefined) => {
            if (!oldData) return undefined;
            return {
              ...oldData,
              ...variables,
            } satisfies FrameworkConfig;
          },
        );
      },
    }),
  );
}

export { deriveRequiredDomainIDs, deriveRequiredControlIDs };

export function useFrameworkConfig(connectionID: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [FRAMEWORK_CONFIG_QUERY, toRef(connectionID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<FrameworkConfig>(
          'GET',
          `/framework/config/connection/${queryKey[1]}`,
        );
        return res.data;
      },
    }),
  );

  const level = computed(() => query.data.value?.level);
  const addOnDomains = computed(() => query.data.value?.addOnDomains || []);
  // 'flexible' when the connection uses policy-driven scope; undefined/'standard'
  // for the level-based scope (SPUD-919).
  const kind = computed(() => query.data.value?.kind);

  const { sortedControls, sortedDomains } = useFramework();

  const configLevels = computed(() => (level.value ? [level.value] : []));
  const frameworkRequiredDomainIDs = computed(() => {
    // Kind-aware set served by the backend (SPUD-982). Fall back to the
    // level-based local computation against older backends — that fallback
    // cannot express a Flexible connection, whose config carries no level and
    // no add-on domains, so it drops every add-on domain a policy reaches into.
    const serverDomainIDs = query.data.value?.requiredDomainIDs;
    return (
      serverDomainIDs ??
      deriveRequiredDomainIDs(
        sortedControls.value,
        sortedDomains.value,
        configLevels.value,
        addOnDomains.value,
      )
    );
  });
  const frameworkRequiredControlIDs = computed(() =>
    deriveRequiredControlIDs(
      sortedControls.value,
      sortedDomains.value,
      configLevels.value,
      addOnDomains.value,
    ),
  );

  return {
    ...query,
    config: query.data,
    level,
    addOnDomains,
    kind,
    frameworkRequiredDomainIDs,
    frameworkRequiredControlIDs,
  };
}

export function useFrameworkRequirements(
  connectionID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  // Kill-switch: when the flag is on, the new /framework/requirements endpoint
  // is bypassed and required control IDs are derived client-side from the
  // framework config — the pre-SPUD-880 behaviour.
  const flags = useFeatureFlags();
  const useFallback = computed(() => flags.ff(FeatureFlag.DisableBECalcFrameworkReqs));

  const endpointQuery = useQuery(
    mergeQueryOptions(
      {
        ...options,
        enabled: computed(() => !useFallback.value && toValue(options?.enabled) !== false),
      },
      {
        queryKey: [FRAMEWORK_REQUIREMENTS_QUERY, toRef(connectionID)] as const,
        async queryFn({ queryKey }) {
          const res = await request<FrameworkRequirementsConnection>(
            'GET',
            `/framework/requirements/connection/${queryKey[1]}`,
          );
          return res.data;
        },
      },
    ),
  );

  const configFallback = useFrameworkConfig(connectionID, {
    ...options,
    enabled: computed(() => useFallback.value && toValue(options?.enabled) !== false),
  });

  const requiredControlIDs = computed(() =>
    useFallback.value
      ? configFallback.frameworkRequiredControlIDs.value
      : endpointQuery.data.value?.requiredControlIDs || [],
  );

  const isLoading = computed(() =>
    useFallback.value ? configFallback.isLoading.value : endpointQuery.isLoading.value,
  );
  const isError = computed(() =>
    useFallback.value ? configFallback.isError.value : endpointQuery.isError.value,
  );
  const isPending = computed(() =>
    useFallback.value ? configFallback.isPending.value : endpointQuery.isPending.value,
  );
  const isSuccess = computed(() =>
    useFallback.value ? configFallback.isSuccess.value : endpointQuery.isSuccess.value,
  );
  const isFetching = computed(() =>
    useFallback.value ? configFallback.isFetching.value : endpointQuery.isFetching.value,
  );
  const error = computed(() =>
    useFallback.value ? configFallback.error.value : endpointQuery.error.value,
  );

  return {
    requiredControlIDs,
    isLoading,
    isError,
    isPending,
    isSuccess,
    isFetching,
    error,
    refetch: () => (useFallback.value ? configFallback.refetch() : endpointQuery.refetch()),
  };
}

export function useUpdateFrameworkConfig(
  options?: MutationOptions<
    FrameworkConfig,
    { connectionID: string; config: FrameworkConfig; supplierID: string }
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ connectionID, config }) {
        const res = await request<FrameworkConfig>(
          'POST',
          `/framework/config/connection/${connectionID}`,
          config,
        );
        return res.data;
      },
      async onSuccess(_data, variables) {
        await queryClient.setQueryData(
          [FRAMEWORK_CONFIG_QUERY, variables.connectionID],
          (oldData: FrameworkConfig | undefined) => {
            if (!oldData) return undefined;
            return {
              ...oldData,
              ...variables.config,
            } satisfies FrameworkConfig;
          },
        );
        await Promise.all([
          // The patch above merges the written config, which carries no
          // requiredDomainIDs, so the server-derived set would otherwise
          // survive the write and leave every view reading it stale.
          queryClient.invalidateQueries({
            queryKey: [FRAMEWORK_CONFIG_QUERY, variables.connectionID],
          }),
          queryClient.invalidateQueries({
            queryKey: [FRAMEWORK_REQUIREMENTS_QUERY, variables.connectionID],
          }),
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_ASSESSMENT_ANSWERS_QUERY, variables.supplierID],
          }),
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_ASSESSMENT_SCOPING_ANSWERS_QUERY, variables.supplierID],
          }),
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_COMMUNITY_SIGNALS_QUERY, variables.supplierID],
          }),
        ]);

        // This is to work around an eventual consistency issue on the backend
        // In non-backend speak, basically, we have to wait a bit for the
        // backend to catch up with itself due to events and heavy computation.
        await new Promise((resolve) => {
          setTimeout(resolve, 500);
        });
        // This one can be removed once we display overview compliance data from the compliance query.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_INFO_QUERY, variables.supplierID],
          }),
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_COMPLIANCE_VTWO_QUERY, variables.supplierID],
          }),
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_REASSESSMENT_STATUS_QUERY, variables.supplierID],
          }),
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_ASSESSMENT_OVERRIDES_QUERY, variables.supplierID],
          }),
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_ASSESSMENT_REMEDIATIONS_QUERY, variables.supplierID],
          }),
          queryClient.invalidateQueries({ queryKey: [SUPPLIERS_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [CLIENT_DASHBOARD_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [RISKS_QUERY] }),
        ]);
      },
    }),
  );
}

export type FlexibleFrameworkPreviewResponse = {
  controlIDs: string[];
  count: number;
  policyCount: number;
  hasRequiredControls: boolean;
};

// Resolves the Flexible required-control set for a set of labels/tags with no
// connection, driving the connection-request scope gate before a connection
// exists. Shares the backend resolver with the connection read, so the preview
// and the eventual write agree (SPUD-919). Reactive: refetches as the applied
// labels/tags change, so callers consume the resolved state directly rather
// than orchestrating the request themselves.
export function useFlexibleFrameworkPreview(
  labelIDs: MaybeRefOrGetter<string[]>,
  tagIDs: MaybeRefOrGetter<string[]>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [FLEXIBLE_PREVIEW_QUERY, toRef(labelIDs), toRef(tagIDs)] as const,
      async queryFn({ queryKey }) {
        const res = await request<FlexibleFrameworkPreviewResponse>(
          'POST',
          '/framework/flexible-preview',
          { labelIDs: queryKey[1], tagIDs: queryKey[2] },
        );
        return res.data;
      },
    }),
  );

  const resolvesControls = computed(() => query.data.value?.hasRequiredControls ?? false);
  const controlCount = computed(() => query.data.value?.count ?? 0);
  const policyCount = computed(() => query.data.value?.policyCount ?? 0);

  return { ...query, resolvesControls, controlCount, policyCount };
}

export function useCombinedFrameworkConfig(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [COMBINED_FRAMEWORK_CONFIG_QUERY],
      async queryFn() {
        const res = await request<CombinedFrameworkConfig>('GET', '/framework/config/supplier');
        return res.data;
      },
    }),
  );

  const addOnDomains = computed(() => query.data.value?.addOnDomains || []);
  const levels = computed(() => query.data.value?.levels || []);

  const { sortedControls, sortedDomains } = useFramework();

  const frameworkRequiredDomains = computed(() => {
    // Kind-aware set served by the backend (SPUD-948). Fall back to the
    // level-based local computation against older backends.
    const serverDomainIDs = query.data.value?.requiredDomainIDs;
    const requiredIDs =
      serverDomainIDs ??
      deriveRequiredDomainIDs(
        sortedControls.value,
        sortedDomains.value,
        levels.value,
        addOnDomains.value,
      );
    return sortedDomains.value.filter((d) => requiredIDs.includes(d.domainID));
  });
  const frameworkRequiredDomainIDs = computed(() =>
    frameworkRequiredDomains.value.map((d) => d.domainID),
  );
  const frameworkNotRequiredDomains = computed(() =>
    sortedDomains.value.filter((d) => !frameworkRequiredDomainIDs.value.includes(d.domainID)),
  );

  const frameworkRequiredControlIDs = computed(() => {
    // Kind-aware set served by the backend (SPUD-948); preserve framework
    // ordering by filtering the sorted controls. Fall back to the level-based
    // local computation against older backends.
    const serverControlIDs = query.data.value?.requiredControlIDs;
    if (serverControlIDs) {
      const required = new Set(serverControlIDs);
      return sortedControls.value.filter((c) => required.has(c.controlID)).map((c) => c.controlID);
    }

    return deriveRequiredControlIDs(
      sortedControls.value,
      sortedDomains.value,
      levels.value,
      addOnDomains.value,
    );
  });

  // Update the vuex store for legacy code to use :(
  const store = useStore();
  watchEffect(() => {
    if (store) {
      store.commit('framework/SET_REQUIRED', {
        requiredDomainIDs: frameworkRequiredDomainIDs.value,
        requiredControlIDs: frameworkRequiredControlIDs.value,
      });
    }
  });

  return {
    ...query,
    query,
    addOnDomains,
    frameworkRequiredDomains,
    frameworkRequiredDomainIDs,
    frameworkNotRequiredDomains,
    frameworkRequiredControlIDs,
  };
}
